/**
 * Host adapter for {@link GfNotebookComponent}.
 *
 * A "notebook" is a multi-page, versioned markdown space backed by the generic
 * wiki service. The same UI is reused by different surfaces — a tracker item's
 * Notebook tab and a user's personal notebook — which differ only in where pages
 * live, how attachments are stored, and what should happen after a mutation.
 *
 * Each surface supplies a NotebookHost describing those differences; everything
 * common (page list, editor, version history, file previews, the markdown guide
 * sidebar) lives in the shared component.
 */
export interface NotebookUploadCallbacks {
    /** Per-chunk progress, used to drive the upload progress bars. */
    progressCallback?: (...args: any[]) => any;
    /** Fired once a file finishes uploading. */
    finishedCallback?: (...args: any[]) => any;
}

export interface NotebookHost {
    /** Wiki section the pages belong to (e.g. 'trackeritem', 'user'). */
    readonly section: string;
    /** Ref id within the section (tracker item id, user id). */
    readonly refId: number;
    /** Whether the viewer may create / edit / delete pages and files. */
    readonly canUpdate: boolean;

    /** Show the public/private visibility toggle and public badge (user notebook). */
    readonly enableVisibility: boolean;
    /** Linkify [[Page]] / [Page] references in view mode and offer click-to-create. */
    readonly enableWikiLinks: boolean;
    /** Offer a "copy public link" action for public pages. */
    readonly enablePublicLinks: boolean;

    /**
     * True when attachments belong to the selected page (user notebook); false when
     * they belong to the host itself and are shared across pages (tracker item).
     */
    readonly attachmentsPerPage: boolean;

    /** Query params passed to WikiService.fetch() to list this host's pages. */
    pageListParams(): any;

    /** Current attachment list (for the page when per-page, else the host's list). */
    listAttachments(page: any): Promise<any[]>;
    /** Upload files and claim them as attachments; resolves when all uploads finish. */
    uploadAttachments(page: any, files: File[], callbacks: NotebookUploadCallbacks): Promise<any>;
    /** Remove a single attachment. */
    deleteAttachment(page: any, file: any): Promise<any>;

    /** Called after any page create / update / delete so the host can sync external state. */
    onChange?(pages: any[]): void;
    /** Called after a version is restored so the host can refresh related data. */
    onVersionsRestored?(page: any): void;
    /** Absolute, shareable URL for a public page (used only when enablePublicLinks). */
    publicPageHref?(page: any): string;

    /** Pagename to auto-select on load, e.g. from a deep-linked URL. */
    readonly initialPagename?: string | null;
    /** Called whenever the selected page changes, so the host can reflect it (e.g. in the URL). */
    onPageSelected?(page: any): void;
}
